/* eslint-disable sonarjs/no-identical-functions */
import * as E from "fp-ts/Either";
import { pipe } from "fp-ts/lib/function";
import * as O from "fp-ts/Option";
import * as TE from "fp-ts/TaskEither";
import featureFlags from "../../../../utils/featureFlags";
import {
  apiPaymentEcommerceClient,
  apiPaymentEcommerceClientV2,
  apiPaymentEcommerceAuthClientV1,
  apiPaymentEcommerceClientWithRetry,
  apiPaymentEcommerceClientWithRetryV2,
  apiPaymentEcommerceAuthClientWithRetryV1,
} from "../../../../utils/api/client";
import { ErrorsType } from "../../../../utils/errors/checkErrorsModel";
import {
  Cart,
  PaymentInstrumentsType,
  PaymentMethod,
  PaymentMethodInfo,
  PaymentInfo,
} from "../../../../features/payment/models/paymentModel";
import { validateSessionWalletCardFormFields } from "../../../../utils/regex/validators";
import {
  getRptIdsFromSession,
  getSessionItem,
  SessionItems,
  setSessionItem,
} from "../../../../utils/storage/sessionStorage";
import { NewTransactionResponse } from "../../../../../generated/definitions/payment-ecommerce/NewTransactionResponse";
import {
  CalculateFeeRequest,
  WalletTypeEnum,
} from "../../../../../generated/definitions/payment-ecommerce-v2/CalculateFeeRequest";
import { CreateSessionResponse } from "../../../../../generated/definitions/payment-ecommerce/CreateSessionResponse";
import { Bundle } from "../../../../../generated/definitions/payment-ecommerce-v2/Bundle";
import { CalculateFeeResponse } from "../../../../../generated/definitions/payment-ecommerce-v2/CalculateFeeResponse";
import {
  PaymentMethodsRequest as PaymentMethodsRequestV2,
  UserTouchpointEnum,
  UserDeviceEnum,
  SortByEnum,
  SortOrderEnum,
} from "../../../../../generated/definitions/payment-ecommerce-v2/PaymentMethodsRequest";
import { PaymentMethodsRequest as PaymentMethodsRequestAuthV1 } from "../../../../../generated/definitions/payment-ecommerce-auth-v1/PaymentMethodsRequest";
import { PaymentNoticeItem } from "../../../../../generated/definitions/payment-ecommerce-v2/PaymentNoticeItem";
import { evaluateFeatureFlag } from "../checkoutFeatureFlagsHelper";
import { getUserDevice } from "../../../device/deviceDetection";
import { getLanguage } from "../../../../utils/paymentMethods/paymentMethodsHelper";
import {
  getDataEntryTypeFromSessionStorage,
  getPaymentInfoFromSessionStorage,
} from "../../../mixpanel/mixpanelTracker";
import { mixpanel } from "../../../mixpanel/mixpanelHelperInit";
import {
  MixpanelEventCategory,
  MixpanelEventsId,
  MixpanelPaymentPhase,
} from "../../../mixpanel/mixpanelEvents";

const mixpanelTrackPspUnavailable = () => {
  const paymentInfo = getPaymentInfoFromSessionStorage();
  mixpanel.track(MixpanelEventsId.PSP_UNAVAILABLE, {
    EVENT_ID: MixpanelEventsId.PSP_UNAVAILABLE,
    EVENT_CATEGORY: MixpanelEventCategory.KO,
    reason: null,
    data_entry: getDataEntryTypeFromSessionStorage(),
    organization_name: paymentInfo?.paName,
    organization_fiscal_code: paymentInfo?.paFiscalCode,
    amount: paymentInfo?.amount,
    expiration_date: paymentInfo?.dueDate,
    payment_phase: MixpanelPaymentPhase.VERIFICA,
  });
};

// ->Promise<Either<string,SessionPaymentMethodResponse>>
export const retrieveCardData = async ({
  paymentId,
  orderId,
  onError,
  onResponseSessionPaymentMethod,
}: {
  paymentId: string;
  orderId: string;
  onError: (e: string) => void;
  onResponseSessionPaymentMethod: (r: any) => void;
}) => {
  const transactionId = pipe(
    getSessionItem(SessionItems.transaction) as NewTransactionResponse,
    O.fromNullable,
    O.map((transaction) => transaction.transactionId),
    O.getOrElse(() => "")
  );

  const bearerAuth = pipe(
    getSessionItem(SessionItems.transaction) as NewTransactionResponse,
    O.fromNullable,
    O.chain((transaction) => O.fromNullable(transaction.authToken)),
    O.getOrElse(() => "")
  );

  const sessionPaymentMethod = await pipe(
    TE.tryCatch(
      () =>
        apiPaymentEcommerceClient.getSessionPaymentMethod({
          bearerAuth,
          id: paymentId,
          orderId,
          "x-transaction-id-from-client": transactionId,
        }),
      (_e) => {
        onError(ErrorsType.CONNECTION);
        return E.toError;
      }
    ),
    TE.fold(
      (_r) => async () => {
        onError(ErrorsType.SERVER);
        return {};
      },
      (myResExt) => async () =>
        pipe(
          myResExt,
          E.fold(
            () => [onError(ErrorsType.GENERIC_ERROR)],
            (myRes) => {
              if (myRes?.status === 200) {
                const sessionPaymentMethodResponse = myRes?.value;
                setSessionItem(
                  SessionItems.sessionPaymentMethod,
                  sessionPaymentMethodResponse
                );
                setSessionItem(SessionItems.paymentMethodInfo, {
                  title: `· · · · ${sessionPaymentMethodResponse.lastFourDigits}`,
                  body: sessionPaymentMethodResponse.expiringDate,
                  icon: sessionPaymentMethodResponse.brand,
                } as PaymentMethodInfo);
                return sessionPaymentMethodResponse;
              } else {
                onError(ErrorsType.GENERIC_ERROR);
                return {};
              }
            }
          )
        )
    )
  )();

  onResponseSessionPaymentMethod(sessionPaymentMethod);
};

// -> Promise<Either<string, BundleOptions>>
export const calculateFees = async ({
  paymentId,
  bin,
  walletId,
  walletType,
  pspId,
  onError,
  onPspNotFound,
  onResponsePsp,
}: {
  paymentId: string;
  bin?: string;
  walletId?: string;
  walletType?: WalletTypeEnum;
  pspId?: string;
  onError: (e: string) => void;
  onPspNotFound: () => void;
  onResponsePsp: (r: any) => void;
}) => {
  const calculateFeeRequest: O.Option<CalculateFeeRequest> = pipe(
    getSessionItem(SessionItems.transaction) as NewTransactionResponse,
    O.fromNullable,
    O.map((transaction) => ({
      bin,
      touchpoint: transaction.clientId || "CHECKOUT",
      paymentNotices: transaction.payments.map((payment) => ({
        paymentAmount: payment.amount,
        primaryCreditorInstitution: payment.rptId.substring(0, 11),
        transferList: payment.transferList.map((transfer) => ({
          creditorInstitution: transfer.paFiscalCode,
          digitalStamp: transfer.digitalStamp,
          transferCategory: transfer.transferCategory,
        })),
      })),
      isAllCCP: transaction.payments[0].isAllCCP,
      walletId,
      walletType,
      idPspList: pspId ? [pspId] : [],
    }))
  );

  const transactionId = pipe(
    getSessionItem(SessionItems.transaction) as NewTransactionResponse,
    O.fromNullable,
    O.map((transaction) => transaction.transactionId),
    O.getOrElse(() => "")
  );

  const bearerAuth = pipe(
    getSessionItem(SessionItems.transaction) as NewTransactionResponse,
    O.fromNullable,
    O.chain((transaction) => O.fromNullable(transaction.authToken)),
    O.getOrElse(() => "")
  );

  const MAX_OCCURENCES_AFM = 2147483647;
  await pipe(
    calculateFeeRequest,
    TE.fromOption(() => {
      onError(ErrorsType.GENERIC_ERROR);
      return E.toError;
    }),
    TE.chain((calculateFeeRequest) =>
      TE.tryCatch(
        () =>
          apiPaymentEcommerceClientWithRetryV2.calculateFees({
            bearerAuth,
            "x-transaction-id-from-client": transactionId,
            id: paymentId,
            maxOccurrences: MAX_OCCURENCES_AFM,
            body: calculateFeeRequest,
          }),
        (_e) => {
          onError(ErrorsType.CONNECTION);
          return E.toError;
        }
      )
    ),
    TE.fold(
      (_r) => async () => {
        onError(ErrorsType.SERVER);
        return {};
      },
      (myResExt) => async () =>
        pipe(
          myResExt,
          E.fold(
            () => [],
            (myRes) => {
              if (myRes?.status === 200) {
                onResponsePsp(myRes?.value);
              } else if (myRes?.status === 404) {
                mixpanelTrackPspUnavailable();
                onPspNotFound();
              } else {
                onError(ErrorsType.GENERIC_ERROR);
              }
              return {};
            }
          )
        )
    )
  )();
};

export const getPaymentMethodHandler = async (onError: (e: string) => void) =>
  pipe(
    TE.tryCatch(
      () =>
        pipe(
          getSessionItem(SessionItems.authToken) as string,
          O.fromNullable,
          O.fold(
            () =>
              apiPaymentEcommerceClientV2.getAllPaymentMethods({
                body: buildPaymentInstrumentMethodHandlerSearchRequest(),
              }),
            (bearerAuth) =>
              apiPaymentEcommerceAuthClientV1.getAllPaymentMethodsAuth({
                "x-rpt-ids": getRptIdsFromSession(),
                bearerAuth,
                body: buildPaymentInstrumentMethodHandlerSearchRequest() as any as PaymentMethodsRequestAuthV1,
              })
          )
        ),
      () => {
        onError(ErrorsType.STATUS_ERROR);
        return E.toError;
      }
    ),
    TE.fold(
      () => async () => {
        onError(ErrorsType.STATUS_ERROR);
        return [];
      },
      (myResExt) => async () =>
        pipe(
          myResExt,
          E.fold(
            () => {
              onError(ErrorsType.GENERIC_ERROR);
              return [];
            },
            (myRes) => {
              switch (myRes.status) {
                case 200:
                  return myRes.value.paymentMethods.map(
                    (p) =>
                      ({
                        id: p.id,
                        name: p.name,
                        description: p.description,
                        status: p.status,
                        methodManagement: p.methodManagement,
                        paymentTypeCode: p.paymentTypeCode,
                        feeRange: p.feeRange,
                        asset: p.paymentMethodAsset,
                        brandAsset: p.paymentMethodsBrandAssets,
                        paymentMethodTypes: p.paymentMethodTypes,
                        metadata: p.metadata,
                      } as PaymentInstrumentsType)
                  );
                case 401:
                  onError(ErrorsType.UNAUTHORIZED);
                  return [];
                default:
                  return [];
              }
            }
          )
        )
    )
  )();

const buildPaymentInstrumentMethodHandlerSearchRequest =
  (): PaymentMethodsRequestV2 => {
    const transaction = getSessionItem(SessionItems.transaction) as
      | NewTransactionResponse
      | undefined;
    const cart = getSessionItem(SessionItems.cart) as Cart | undefined;
    const paymentInfo = getSessionItem(SessionItems.paymentInfo) as PaymentInfo;
    const cartClientId = getSessionItem(SessionItems.cartClientId) as
      | string
      | undefined;
    // eslint-disable-next-line functional/no-let
    let userTouchpoint = UserTouchpointEnum.CHECKOUT;
    switch (cartClientId) {
      case "CHECKOUT":
        userTouchpoint = UserTouchpointEnum.CHECKOUT;
        break;
      case "CHECKOUT_CART":
      case "WISP_REDIRECT":
        userTouchpoint = UserTouchpointEnum.CHECKOUT_CART;
        break;
      default:
        userTouchpoint = UserTouchpointEnum.CHECKOUT;
        break;
    }
    // eslint-disable-next-line functional/no-let
    let totalAmount: number;
    // eslint-disable-next-line functional/no-let
    let paymentNotices: Array<PaymentNoticeItem>;
    // eslint-disable-next-line functional/no-let
    let allCCp: boolean | undefined;
    // if transaction is activated take amount from transaction object (that is actualizated)
    if (
      transaction &&
      transaction.payments &&
      transaction.payments.length > 0
    ) {
      totalAmount = transaction.payments.reduce(
        (sum: number, payment) => sum + Number(payment.amount),
        0
      );
      paymentNotices = transaction.payments.map((p) => ({
        paymentAmount: p.amount,
        primaryCreditorInstitution: p.rptId.substring(0, 11),
        transferList: p.transferList.map((t) => ({
          creditorInstitution: t.paFiscalCode,
          digitalStamp: t.digitalStamp,
          transferCategory: t.transferCategory,
        })),
      }));
      allCCp = transaction.payments[0].isAllCCP;
    }
    // otherwise check if we are in a cart payment
    else if (cart) {
      totalAmount = cart.paymentNotices.reduce(
        (sum: number, payment) => sum + Number(payment.amount),
        0
      );
      paymentNotices = cart.paymentNotices.map((p) => ({
        paymentAmount: p.amount,
        primaryCreditorInstitution: p.fiscalCode,
      }));
    }
    // or in a single payment notice case
    else {
      totalAmount = paymentInfo.amount;
      paymentNotices = [
        {
          paymentAmount: paymentInfo.amount,
          primaryCreditorInstitution: paymentInfo.rptId?.substring(0, 11) ?? "",
        },
      ];
    }

    // detect browser type (Safari vs others)
    const userDevice = getUserDevice() as UserDeviceEnum | undefined;
    const language = getLanguage();
    const sortBy = SortByEnum.DESCRIPTION;
    const sortOrder = SortOrderEnum.ASC;
    const priorityGroups = ["CP"];
    return {
      userTouchpoint,
      userDevice,
      totalAmount,
      language,
      sortBy,
      sortOrder,
      priorityGroups,
      paymentNotice: paymentNotices,
      allCCp,
    };
  };

const evaluatePaymentMethodHandlerEnabledFF = async (): Promise<boolean> => {
  // eslint-disable-next-line functional/no-let
  let featureFlag = getSessionItem(
    SessionItems.enablePaymentMethodsHandler
  ) as string;
  if (featureFlag === null || featureFlag === undefined) {
    // ff not found in session storage, invoking ff api
    await evaluateFeatureFlag(
      featureFlags.enablePaymentMethodsHandler,
      (e: string) => {
        // eslint-disable-next-line no-console
        console.error(
          `Error while getting feature flag ${featureFlags.enablePaymentMethodsHandler}`,
          e
        );
      },
      (data: { enabled: boolean }) => {
        setSessionItem(
          SessionItems.enablePaymentMethodsHandler,
          data.enabled.toString()
        );
        featureFlag = data.enabled.toString();
      }
    );
  }
  return featureFlag === "true";
};

export const getPaymentInstruments = async (
  onError: (e: string) => void,
  onResponse: (data: Array<PaymentInstrumentsType>) => void
) => {
  const list = await pipe(
    await evaluatePaymentMethodHandlerEnabledFF(),
    O.fromNullable,
    O.filter((ff) => ff),
    O.foldW(
      () => {
        onError(ErrorsType.STATUS_ERROR);
        return [];
      },
      () => getPaymentMethodHandler(onError)
    )
  );
  onResponse(list as any as Array<PaymentInstrumentsType>);
};

export const npgSessionsFields = async (
  onError: (e: string) => void,
  onResponse: (data: CreateSessionResponse) => void
) =>
  await pipe(
    TE.tryCatch(
      () => {
        const paymentMethodId =
          (
            getSessionItem(SessionItems.paymentMethod) as
              | PaymentMethod
              | undefined
          )?.paymentMethodId || "";
        const payload = {
          id: paymentMethodId,
          lang: localStorage.getItem("i18nextLng") ?? "it",
        };
        return pipe(
          getSessionItem(SessionItems.authToken) as string,
          O.fromNullable,
          O.fold(
            () =>
              apiPaymentEcommerceClientWithRetry.createSession({
                ...payload,
              }),
            (bearerAuth) =>
              apiPaymentEcommerceAuthClientWithRetryV1.createSessionAuth({
                "x-rpt-ids": getRptIdsFromSession(),
                bearerAuth,
                ...payload,
              })
          )
        );
      },
      () => {
        onError("NPG_NET_ERR");
        return "NPG_NET_ERR";
      }
    ),
    TE.fold(
      (err) => TE.left(err),
      (myResExt) => async () =>
        pipe(
          myResExt,
          E.fold(
            () => ({}),
            (myRes) => {
              switch (myRes.status) {
                case 200:
                  pipe(
                    myRes.value.paymentMethodData.form,
                    validateSessionWalletCardFormFields,
                    O.match(
                      () => onError("NPG_RESP_ERROR"),
                      () => onResponse(myRes.value)
                    )
                  );
                  return myRes;
                case 401:
                  onError(ErrorsType.UNAUTHORIZED);
                  return {};
                default:
                  return {};
              }
            }
          )
        )
    )
  )();

export const getFees = (
  onSuccess: (value: boolean) => void,
  onPspNotFound: () => void,
  onError: (m: string) => void,
  bin?: string
) =>
  calculateFees({
    paymentId:
      (getSessionItem(SessionItems.paymentMethod) as PaymentMethod | undefined)
        ?.paymentMethodId || "",
    bin,
    walletId:
      (getSessionItem(SessionItems.paymentMethod) as PaymentMethod | undefined)
        ?.walletId ?? undefined,
    walletType:
      (getSessionItem(SessionItems.paymentMethod) as PaymentMethod | undefined)
        ?.walletType ?? undefined,
    pspId:
      (getSessionItem(SessionItems.paymentMethod) as PaymentMethod | undefined)
        ?.pspId ?? undefined,
    onError,
    onPspNotFound,
    onResponsePsp: (resp) => {
      pipe(
        resp,
        CalculateFeeResponse.decode,
        O.fromEither,
        O.chain((resp) => O.fromNullable(resp.belowThreshold)),
        O.fold(
          () => onError(ErrorsType.GENERIC_ERROR),
          (value) => {
            const firstPsp = pipe(
              resp?.bundles,
              O.fromNullable,
              O.chain((sortedArray) => O.fromNullable(sortedArray[0])),
              O.map((a) => a as Bundle),
              O.getOrElseW(() => ({}))
            );

            setSessionItem(SessionItems.pspSelected, firstPsp);
            onSuccess(value);
          }
        )
      );
    },
  });
